import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Site } from '@app/models/site.model';
import { SiteService } from '../services/site.service';

@Component({
  selector: 'app-site-switcher',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (sites.length > 0) {
      <div class="site-switcher" [class.disabled]="sites.length < 2">
        <button
          type="button"
          class="site-switcher-btn"
          [disabled]="sites.length < 2"
          (click)="toggle($event)"
          [attr.aria-expanded]="open"
          [title]="'SITES.SWITCHER_LABEL' | translate">
          <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
          <span class="site-switcher-label">
            {{ selectedSite?.label || ('SITES.SWITCHER_PLACEHOLDER' | translate) }}
          </span>
          @if (sites.length >= 2) {
            <svg class="w-3 h-3 flex-shrink-0 transition-transform" [class.rotate-180]="open" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          }
        </button>

        @if (open && sites.length >= 2) {
          <div class="site-switcher-dropdown">
            @for (site of sites; track site.identifier) {
              <button
                type="button"
                class="site-switcher-item"
                [class.active]="site.identifier === selectedSite?.identifier"
                (click)="onSelect(site)">
                <span class="font-semibold truncate">{{ site.label }}</span>
                @if (site.address) {
                  <span class="text-xs truncate" style="color: #94a3b8;">{{ site.address }}</span>
                }
              </button>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [`
    .site-switcher {
      position: relative;
      display: inline-flex;
    }
    .site-switcher-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.4rem 0.75rem;
      font-size: 0.8125rem;
      font-weight: 600;
      border-radius: 0.625rem;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #e2e8f0;
      cursor: pointer;
      transition: all 0.15s ease;
      max-width: 220px;
    }
    .site-switcher-btn:hover:not(:disabled) {
      background: rgba(99, 102, 241, 0.16);
      border-color: rgba(99, 102, 241, 0.35);
      color: #ffffff;
    }
    .site-switcher-btn:disabled {
      opacity: 0.65;
      cursor: default;
    }
    .site-switcher-btn:focus-visible {
      outline: 2px solid rgba(99, 102, 241, 0.6);
      outline-offset: 2px;
    }
    .site-switcher-label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 140px;
    }
    .rotate-180 {
      transform: rotate(180deg);
    }
    .site-switcher-dropdown {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      min-width: 240px;
      max-height: 320px;
      overflow-y: auto;
      background: #111a33;
      border: 1px solid rgba(99, 102, 241, 0.2);
      border-radius: 0.75rem;
      padding: 6px;
      box-shadow: 0 16px 36px rgba(10, 16, 32, 0.55), 0 2px 6px rgba(10, 16, 32, 0.25);
      z-index: 50;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .site-switcher-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      padding: 8px 10px;
      border-radius: 0.5rem;
      background: transparent;
      border: none;
      color: #e2e8f0;
      cursor: pointer;
      text-align: left;
      transition: background 0.12s ease, color 0.12s ease;
      font-size: 0.8125rem;
    }
    .site-switcher-item:hover {
      background: rgba(99, 102, 241, 0.16);
      color: #ffffff;
    }
    .site-switcher-item.active {
      background: rgba(99, 102, 241, 0.22);
      color: #ffffff;
      box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.35);
    }
  `]
})
export class SiteSwitcherComponent implements OnInit {
  private siteService = inject(SiteService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);

  sites: Site[] = [];
  selectedSite: Site | null = null;
  open = false;

  ngOnInit() {
    this.siteService.sites$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(sites => {
      this.sites = sites;
      this.cdr.markForCheck();
    });

    this.siteService.selectedSite$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(site => {
      this.selectedSite = site;
      this.cdr.markForCheck();
    });
  }

  toggle(event: Event) {
    event.stopPropagation();
    if (this.sites.length < 2) return;
    this.open = !this.open;
    this.cdr.markForCheck();
  }

  onSelect(site: Site) {
    if (site.identifier !== this.selectedSite?.identifier) {
      this.siteService.selectSite(site);
    }
    this.open = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.open) return;
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }
}
